/**
 * Issues Summary Hook
 *
 * Fetches issue counts grouped by type from the dedicated summary endpoint.
 * Uses the same base filters as the issues list, so counts always match the table.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

export interface IssuesSummary {
    total: number;
    missing_document: number;
    single_document_check: number;
    cross_check_document_check: number;
    non_document: number;
}

export interface IssuesSummaryParams {
    show_resolved?: boolean;
    start_date?: string;
    end_date?: string;
}

export const ISSUES_SUMMARY_QUERY_KEYS = {
    all: ["issues-summary"] as const,
    summary: (params: IssuesSummaryParams) =>
        [...ISSUES_SUMMARY_QUERY_KEYS.all, params] as const,
};

export function useIssuesSummary(params: IssuesSummaryParams, enabled = true) {
    return useQuery({
        queryKey: ISSUES_SUMMARY_QUERY_KEYS.summary(params),
        queryFn: async (): Promise<IssuesSummary> => {
            const queryParams: Record<string, string> = {};
            if (params.show_resolved !== undefined) queryParams.show_resolved = String(params.show_resolved);
            if (params.start_date) queryParams.start_date = params.start_date;
            if (params.end_date) queryParams.end_date = params.end_date;

            return await apiGet<IssuesSummary>(
                "/container-issues/issues/summary",
                { params: queryParams },
            );
        },
        enabled,
        staleTime: 30_000,
    });
}
